import React, { useEffect, memo } from 'react';
import { useMEMTranslation } from '../../utils/text-utils';
import { useTrackInteraction } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';
import { useAvailableParticipations } from './hooks';
import { Option, Combobox } from '../../providers/harmony-scope/lazy-enablers';
import { useMonthDayYear, useMonthAndYear } from '../../utils/date-utils';
import { getDefaultParticipation } from '../../ducks/filters/utils-filters';

interface IProps {
  selectedFiscalYear: string;
  selectedParticipation: string;
  onChange: (participation: string) => void;
}

const componentName = 'Participation Picker Filters Panel';
export const ComponentFilterParticipation = memo((props: IProps) => {
  // Props Accessors
  const { selectedFiscalYear: selectedFiscalYearProp, selectedParticipation: selectedParticipationProp, onChange } = props;

  // Hooks
  const monthDayYear = useMonthDayYear();
  const monthAndYear = useMonthAndYear();
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const trackInteraction = useTrackInteraction();
  const availableParticipations = useAvailableParticipations(selectedFiscalYearProp);
  const [selectedParticipation, setSelectedParticipation] = React.useState(selectedParticipationProp);

  // Effects
  useEffect(() => {
    const selectedParticipationForFiscalYear = availableParticipations.find(
      (participation) => `${participation.participationId}` === selectedParticipationProp
    );
    const participationToSelect =
      selectedParticipationForFiscalYear || getDefaultParticipation(availableParticipations) || availableParticipations[0];

    setSelectedParticipation(participationToSelect ? `${participationToSelect.participationId}` : '');
  }, [availableParticipations, selectedFiscalYearProp, selectedParticipationProp]);

  useEffect(() => {
    onChange(selectedParticipation);
  }, [onChange, selectedParticipation]);

  // Handlers
  const participationChanged = (event: any) => {
    if (!event.target.value) return;
    const newParticipation = event.target.value;
    trackInteraction('Selected paticipation', pageName, componentName, {
      selectedParticipation: newParticipation,
    });
    setSelectedParticipation(newParticipation);
  };

  return availableParticipations.length > 0 ? (
    <>
      <Combobox label={t('filtersPanelRoleLabel')} value={selectedParticipation} onHeChange={participationChanged}>
        {availableParticipations.map((p) => {
          // ParticipationId 0 is for NPMs
          if (p.participationId === 0) {
            return (
              <Option key={`${p.participationId}`} value={`${p.participationId}`}>
                {`${p.roleSummary}`}
              </Option>
            );
          }

          return (
            <Option
              key={`${p.participationId}`}
              value={`${p.participationId}`}
              title={`${monthDayYear(p.participationStartDateTime)} - ${monthDayYear(p.participationEndDateTime)}`}
            >
              {`${p.roleSummary} (${monthAndYear(p.participationStartDateTime)})`}
            </Option>
          );
        })}
      </Combobox>
      <br />
    </>
  ) : null;
});
